import React, { useState, useEffect, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { graphConfig } from "./graphConfig";
import {
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  TextField,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import GroupIcon from "@mui/icons-material/Group";

const GroupList = ({ onSelectGroup, selectedGroupIds }) => {
  const { instance, accounts } = useMsal();
  const [groups, setGroups] = useState([]);
  const [searchTerm, setSearchTerm] = useState("AAD-USERS-BHG-SMS");
  const listRef = useRef();
  const [sortedGroups, setSortedGroups] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchGroups = async () => {
    setLoading(true);
    try {
      const request = {
        scopes: ["Group.Read.All"],
        account: accounts[0],
      };

      const response = await instance.acquireTokenSilent(request);

      let groups = [];
      let nextLink = `${graphConfig.groupList.endpoint}`;

      while (nextLink) {
        const groupsResponse = await fetch(nextLink, {
          headers: {
            Authorization: `Bearer ${response.accessToken}`,
          },
        });
        const groupsData = await groupsResponse.json();
        groups = groups.concat(groupsData.value);
        nextLink = groupsData["@odata.nextLink"];
      }

      const filteredGroups = groups.filter((group) => group);

      setGroups(filteredGroups);
    } catch (error) {
      console.error("Fehler beim Abrufen der Gruppen:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGroups();
  }, [instance, accounts]);

  useEffect(() => {
    const filteredGroups = groups.filter((group) =>
      group.displayName.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const sortedGroups = [...filteredGroups].sort((a, b) => {
      const aSelected = selectedGroupIds.includes(a.id);
      const bSelected = selectedGroupIds.includes(b.id);
      return aSelected === bSelected ? 0 : aSelected ? -1 : 1;
    });

    setSortedGroups(sortedGroups);
  }, [groups, searchTerm, selectedGroupIds]);

  const handleGroupClick = (groupId) => {
    if (selectedGroupIds.includes(groupId)) {
      onSelectGroup(groupId);
      fetchGroups(); // Trigger new search when group is deactivated
    } else {
      onSelectGroup(groupId);
    }
  };

  const handleSearchChange = async (event) => {
    const searchTerm = event.target.value;
    setSearchTerm(searchTerm);

    if (searchTerm.length > 2) {
      // Only search if the term is longer than 2 characters
      try {
        const request = {
          scopes: ["Group.Read.All"],
          account: accounts[0],
        };

        const response = await instance.acquireTokenSilent(request);

        const searchResponse = await fetch(
          `${graphConfig.groupList.endpoint}?$filter=startswith(displayName,'${searchTerm}')`,
          {
            headers: {
              Authorization: `Bearer ${response.accessToken}`,
            },
          }
        );
        const searchData = await searchResponse.json();

        const selectedGroups = groups.filter((group) =>
          selectedGroupIds.includes(group.id)
        );
        const searchResults = searchData.value.filter(
          (group) => !selectedGroupIds.includes(group.id)
        );
        const allGroups = [...selectedGroups, ...searchResults];
        setGroups(allGroups);
      } catch (error) {
        console.error("Fehler bei der Gruppensuche:", error);
      }
    } else {
      setGroups(groups.filter((group) => selectedGroupIds.includes(group.id))); // Show only selected groups if search term is too short
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={{ position: "sticky", top: 0, zIndex: 1 }}>
        <TextField
          label="Gruppen suchen"
          variant="outlined"
          fullWidth
          value={searchTerm}
          onChange={handleSearchChange}
          style={{ marginBottom: "20px" }}
        />
      </div>
      <List
        ref={listRef}
        style={{
          flexGrow: 1,
          overflowY: "auto",
          borderRadius: "4px",
          border: "1px solid #c4c4c4",
          paddingTop: 0,
          paddingBottom: 0,
          backgroundColor: "white",
        }}
      >
        {!searchTerm && groups.length === 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              padding: "20px",
              color: "#666",
              textAlign: "center",
              fontWeight: "normal",
              fontSize: "1.1rem",
            }}
          >
            Geben Sie einen Gruppennamen ein, um die Suche zu starten
          </div>
        )}
        {sortedGroups.map((group) => {
          return (
            <ListItem
              key={group.id}
              button="true"
              selected={selectedGroupIds.includes(group.id)}
              onClick={() => handleGroupClick(group.id)}
              sx={{
                bgcolor: selectedGroupIds.includes(group.id)
                  ? "primary.main"
                  : "background.paper",
                color: selectedGroupIds.includes(group.id)
                  ? "white"
                  : "inherit",
                "&:hover": {
                  bgcolor: selectedGroupIds.includes(group.id)
                    ? "primary.dark"
                    : "action.hover",
                },
              }}
            >
              <ListItemIcon>
                <GroupIcon
                  sx={{
                    color: selectedGroupIds.includes(group.id)
                      ? "white"
                      : "inherit",
                  }}
                />
              </ListItemIcon>
              <ListItemText
                primary={group.displayName}
                primaryTypographyProps={{
                  color: selectedGroupIds.includes(group.id)
                    ? "white"
                    : "inherit",
                }}
                secondary={null}
                secondaryTypographyProps={{}}
              />
            </ListItem>
          );
        })}
      </List>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </div>
  );
};

export default GroupList;
